import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const BuildDebugPanel = ({ currentProject, currentFile, onAutoFix }) => {
  const [buildOutput, setBuildOutput] = useState('');
  const [buildCommand, setBuildCommand] = useState('npm test');
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildHistory, setBuildHistory] = useState([]);
  const [errorDiagnostic, setErrorDiagnostic] = useState(null);
  const [isAutoFixing, setIsAutoFixing] = useState(false);
  const [autoFixResult, setAutoFixResult] = useState(null);
  const [backendHealth, setBackendHealth] = useState('unknown');

  // Check backend health periodically
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await apiService.checkHealth();
        setBackendHealth(health.status);
      } catch (error) {
        setBackendHealth('unhealthy');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 15000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, []);

  const executeBuild = async () => {
    if (!currentProject) {
      setBuildOutput('Error: No project selected');
      return;
    }

    setIsBuilding(true);
    setBuildOutput('Executing build command...\n');

    try {
      const result = await apiService.executeBuild({
        project_id: currentProject.id,
        command: buildCommand,
        working_directory: '.',
        timeout: 120
      });

      const output = `
=== Build Execution ===
Command: ${buildCommand}
Exit Code: ${result.exit_code}
Execution Time: ${result.execution_time.toFixed(2)}s
Success: ${result.success ? '✓ Yes' : '✗ No'}

=== Output ===
${result.output}

${result.errors ? `=== Errors ===\n${result.errors}` : ''}
`;

      setBuildOutput(output);

      // Add to history
      const historyEntry = {
        timestamp: new Date().toISOString(),
        command: buildCommand,
        success: result.success,
        exitCode: result.exit_code,
        executionTime: result.execution_time
      };
      setBuildHistory(prev => [historyEntry, ...prev].slice(0, 10));

      // Auto-diagnose if there are errors
      if (!result.success && result.errors) {
        diagnoseError(result.errors);
      }
    } catch (error) {
      setBuildOutput(`Error executing build: ${error.message}`);
    } finally {
      setIsBuilding(false);
    }
  };

  const diagnoseError = async (errorMessage) => {
    try {
      const diagnostic = await apiService.diagnoseError({
        error_message: errorMessage,
        file_content: currentFile?.content,
        file_path: currentFile?.path,
        language: currentFile?.language || 'javascript'
      });

      setErrorDiagnostic(diagnostic);
    } catch (error) {
      console.error('Error diagnosing:', error);
    }
  };

  const autoFix = async () => {
    if (!currentFile) {
      alert('No file selected');
      return;
    }

    setIsAutoFixing(true);
    setAutoFixResult(null);

    try {
      const result = await apiService.autoFixFile({
        file_id: currentFile.id,
        max_attempts: 3,
        verify_fix: true
      });

      setAutoFixResult(result);

      if (result.success && onAutoFix) {
        onAutoFix(result.fixed_code);
      }
    } catch (error) {
      setAutoFixResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsAutoFixing(false);
    }
  };

  const getHealthStatusColor = () => {
    switch (backendHealth) {
      case 'healthy':
        return 'text-green-500';
      case 'degraded':
        return 'text-yellow-500';
      case 'unhealthy':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getHealthStatusIcon = () => {
    switch (backendHealth) {
      case 'healthy':
        return '●';
      case 'degraded':
        return '◐';
      case 'unhealthy':
        return '○';
      default:
        return '?';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Build & Debug</h2>
        <div className={`flex items-center gap-2 ${getHealthStatusColor()}`}>
          <span className="text-xl">{getHealthStatusIcon()}</span>
          <span className="text-sm capitalize">{backendHealth}</span>
        </div>
      </div>

      {/* Build Command */}
      <div className="p-4 border-b border-gray-700">
        <label className="block text-sm font-medium mb-2">Build Command</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={buildCommand}
            onChange={(e) => setBuildCommand(e.target.value)}
            className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="npm test"
            disabled={isBuilding}
          />
          <button
            onClick={executeBuild}
            disabled={isBuilding || !currentProject}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded text-sm font-medium"
          >
            {isBuilding ? 'Building...' : 'Run'}
          </button>
        </div>

        {/* Quick commands */}
        <div className="flex gap-2 mt-2 flex-wrap">
          <button
            onClick={() => setBuildCommand('npm test')}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
          >
            npm test
          </button>
          <button
            onClick={() => setBuildCommand('npm run build')}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
          >
            npm build
          </button>
          <button
            onClick={() => setBuildCommand('npm run lint')}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
          >
            npm lint
          </button>
          <button
            onClick={() => setBuildCommand('python -m pytest')}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
          >
            pytest
          </button>
        </div>
      </div>

      {/* Build Output */}
      <div className="flex-1 overflow-auto p-4">
        <pre className="text-xs font-mono whitespace-pre-wrap bg-black rounded p-3">
          {buildOutput || 'No build output yet. Click "Run" to execute a build command.'}
        </pre>

        {/* Error Diagnostic */}
        {errorDiagnostic && (
          <div className="mt-4 bg-red-900 bg-opacity-20 border border-red-500 rounded p-3">
            <h3 className="font-semibold mb-2 text-red-400">Error Diagnostic</h3>
            <div className="text-sm space-y-2">
              <p><strong>Type:</strong> {errorDiagnostic.error_type}</p>
              <p><strong>Message:</strong> {errorDiagnostic.message}</p>
              {errorDiagnostic.suggested_fix && (
                <div>
                  <strong>Suggested Fix:</strong>
                  <pre className="mt-1 bg-black rounded p-2 text-xs whitespace-pre-wrap">
                    {errorDiagnostic.suggested_fix}
                  </pre>
                </div>
              )}
              <button
                onClick={autoFix}
                disabled={isAutoFixing || !currentFile}
                className="mt-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-3 py-1 rounded text-sm"
              >
                {isAutoFixing ? 'Auto-fixing...' : 'Auto-fix This File'}
              </button>
            </div>
          </div>
        )}

        {/* Auto-fix Result */}
        {autoFixResult && (
          <div className={`mt-4 border rounded p-3 ${
            autoFixResult.success ? 'bg-green-900 bg-opacity-20 border-green-500' : 'bg-red-900 bg-opacity-20 border-red-500'
          }`}>
            <h3 className={`font-semibold mb-2 ${autoFixResult.success ? 'text-green-400' : 'text-red-400'}`}>
              Auto-fix Result
            </h3>
            <div className="text-sm space-y-1">
              <p><strong>Success:</strong> {autoFixResult.success ? '✓ Yes' : '✗ No'}</p>
              <p><strong>Attempts:</strong> {autoFixResult.attempts}</p>
              {autoFixResult.issues_found && (
                <p><strong>Issues Found:</strong> {autoFixResult.issues_found.length}</p>
              )}
              {autoFixResult.issues_fixed && (
                <p><strong>Issues Fixed:</strong> {autoFixResult.issues_fixed.length}</p>
              )}
              {autoFixResult.verification_output && (
                <p><strong>Verification:</strong> {autoFixResult.verification_output}</p>
              )}
              {autoFixResult.error && (
                <p className="text-red-300"><strong>Error:</strong> {autoFixResult.error}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Build History */}
      {buildHistory.length > 0 && (
        <div className="p-4 border-t border-gray-700">
          <h3 className="text-sm font-semibold mb-2">Recent Builds</h3>
          <div className="space-y-1 max-h-32 overflow-auto">
            {buildHistory.map((entry, index) => (
              <div key={index} className="text-xs flex justify-between items-center bg-gray-800 rounded px-2 py-1">
                <span className={entry.success ? 'text-green-400' : 'text-red-400'}>
                  {entry.success ? '✓' : '✗'} {entry.command}
                </span>
                <span className="text-gray-400">
                  {entry.executionTime.toFixed(1)}s
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildDebugPanel;
